import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { expertDashboardApi } from '../../../lib/http';
import toast from 'react-hot-toast';
import type { ExpertClientDetail } from '../../../types/client';

interface ClientDetailHeaderProps {
  client: ExpertClientDetail;
}

const ClientDetailHeader: React.FC<ClientDetailHeaderProps> = ({ client }) => {
  const queryClient = useQueryClient();

  const toggleFavoriteMutation = useMutation({
    mutationFn: () => expertDashboardApi.expertClients.toggleFavorite(client.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert-client', client.id] });
      toast.success(client.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    },
    onError: () => {
      toast.error('Failed to update favorite status');
    },
  });

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
          {getInitials(client.effective_name)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              {client.effective_name}
            </h1>
            <button
              onClick={() => toggleFavoriteMutation.mutate()}
              disabled={toggleFavoriteMutation.isPending}
              className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={client.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg
                className={`w-6 h-6 ${client.is_favorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                fill={client.is_favorite ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              client.linked ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {client.linked ? 'Client' : 'Contact'}
            </span>
          </div>

          {/* Contact info */}
          <div className="space-y-1">
            {client.effective_email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {client.effective_email}
              </div>
            )}
            {client.effective_phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {client.effective_phone}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tags */}
      {client.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {client.tags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientDetailHeader;
